import React from "react";
import { Box, Button, ButtonGroup, type ButtonGroupProps, useToken } from "@chakra-ui/react";
import { useReducedMotion } from "../../theme/foundations/motion";
import { motion } from "framer-motion";

export interface SegmentedOption<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Optional count chip after the label (tickets status strip). */
  count?: number;
}

export interface SegmentedControlProps<T extends string> extends Omit<ButtonGroupProps, "onChange" | "size"> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Unique per control — the sliding white pill's framer layoutId (default "segment"). */
  layoutId?: string;
  size?: "sm" | "md";
  "aria-label": string;
}

/**
 * Segmented control built from Button variant="segment" in a sunken track, with a spring-sliding white pill (layoutId).
 * @example <SegmentedControl aria-label="Spend period" layoutId="spend-segment" value={period} onChange={setPeriod} options={[{ value: "6m", label: "6 months" }, …]} />
 */
export function SegmentedControl<T extends string>({ options, value, onChange, layoutId = "segment", size = "md", ...rest }: SegmentedControlProps<T>) {
  const reduce = useReducedMotion();
  const [pillBg] = useToken("colors", ["surface.card"]);
  const [pillShadow] = useToken("shadows", ["xs"]);
  return (
    <ButtonGroup role="group" spacing={1} p={1} bg="surface.sunken" borderRadius="md" isAttached={false} {...rest}>
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <Button
            key={opt.value}
            variant="segment"
            size={size}
            h={size === "sm" ? "1.875rem" : "2.25rem"}
            aria-pressed={selected}
            onClick={() => onChange(opt.value)}
            bg="transparent"
            boxShadow="none"
            sx={{ "&[aria-pressed=true]": { color: "text.primary", bg: "transparent", boxShadow: "none", _focusVisible: { boxShadow: "outline" } } }}
          >
            {selected && (
              <motion.span
                layoutId={layoutId}
                aria-hidden
                style={{ position: "absolute", inset: 0, background: pillBg, borderRadius: 6, boxShadow: pillShadow, zIndex: 0 }}
                transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 420, damping: 34 }}
              />
            )}
            <Box as="span" position="relative" zIndex={1} display="inline-flex" alignItems="center" gap={1.5}>
              {opt.label}
              {typeof opt.count === "number" && (
                <Box
                  as="span"
                  textStyle="caption"
                  bg={selected ? "navy.50" : "sand.200"}
                  color="text.secondary"
                  borderRadius="full"
                  px={1.5}
                  minW="1.25rem"
                  textAlign="center"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {opt.count}
                </Box>
              )}
            </Box>
          </Button>
        );
      })}
    </ButtonGroup>
  );
}
